import { Cell, Text, Tooltip, VFlow } from 'bold-ui'
import { DecimalField, FractionalField } from 'components/form'
import { FormaFarmaceuticaSelectField } from 'components/form/field/select/FormaFarmaceuticaSelectField'
import { UnidadeMedidaSelectField } from 'components/form/field/select/UnidadeMedidaSelectField'
import { usePecField } from 'components/form/final-form/hooks/useField'
import React from 'react'
import { MetaRoot } from 'util/metaPath'
import { handleKeyEnter } from 'view/atendimentos/detail/soap/util-soap'

import { MedicamentoFormModel, TipoDoseEnum } from '../../../model-prescricao'
import {
  RENOVACAO_TOOLTIP_TEXT,
  UTILIZE_VIRGULA_DOSES_FRACIONADAS_TOOLTIP_TEXT,
  UTILIZE_VIRGULA_OU_BARRA_DOSES_FRACIONADAS_TOOLTIP_TEXT,
} from '../../utils/constants-prescricao'
import { TipoDoseRadioField } from '../TipoDoseRadioField'

interface DoseCellsSectionProps {
  name: MetaRoot<MedicamentoFormModel>
  onTipoDoseChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  hasTotalDoseError: boolean
  isRenovacao: boolean
}

const DOSE_PRECISAO_DECIMAL = 2
const DOSE_MAX_LENGTH = 5
const DOSE_FRACIONARIA_PRECISAO = 1

export const DoseCellsSection = ({
  name,
  onTipoDoseChange,
  hasTotalDoseError,
  isRenovacao,
}: Readonly<DoseCellsSectionProps>) => {
  const { input: tipoDoseInput } = usePecField({ name: name.tipoDose, subscription: { value: true } })
  const tipoDose = tipoDoseInput.value

  const { input: registroManualInput } = usePecField({ name: name.registroManual, subscription: { value: true } })
  const isRegistroManual = registroManualInput.value

  const { input: principioAtivoInput } = usePecField({ name: name.principioAtivoCombo, subscription: { value: true } })
  const principioAtivoCombo = principioAtivoInput.value

  const isDisabled = !principioAtivoCombo && !isRegistroManual

  return (
    <>
      <Cell size={7}>
        <TipoDoseRadioField name={name.tipoDose} isRenovacao={isRenovacao} onChange={onTipoDoseChange} />
      </Cell>

      {!isRegistroManual ? (
        <Cell size={5} data-cy='FormaFarmaceutica'>
          <VFlow vSpacing={0.2}>
            <Text fontWeight='bold'>Forma farmacêutica</Text>
            <Text data-cy='FormaFarmaceuticaLabel'>
              {principioAtivoCombo?.medicamento?.formaFarmaceutica?.nome ?? '-'}
            </Text>
          </VFlow>
        </Cell>
      ) : (
        <Cell size={5} data-cy='FormaFarmaceutica'>
          <Tooltip text={isRenovacao ? RENOVACAO_TOOLTIP_TEXT : undefined}>
            <FormaFarmaceuticaSelectField
              name={name.formaFarmaceutica}
              label='Forma farmacêutica'
              onKeyUp={handleKeyEnter}
              required
              data-cy='FormaFarmaceuticaCombobox'
              disabled={isRenovacao}
            />
          </Tooltip>
        </Cell>
      )}

      {tipoDose !== TipoDoseEnum.FRACIONADA ? (
        <Cell size={3} data-cy='Dose'>
          <Tooltip placement='bottom-start' text={UTILIZE_VIRGULA_OU_BARRA_DOSES_FRACIONADAS_TOOLTIP_TEXT}>
            <FractionalField
              name={name.qtDose}
              label='Quantidade da dose'
              required
              maxLength={DOSE_MAX_LENGTH}
              precisaoDecimal={DOSE_PRECISAO_DECIMAL}
              precisaoFracionaria={DOSE_FRACIONARIA_PRECISAO}
              disabled={isDisabled}
              data-cy='DoseTextField'
            />
          </Tooltip>
        </Cell>
      ) : (
        <Cell size={4} data-cy='Dose'>
          <Tooltip placement='bottom-start' text={UTILIZE_VIRGULA_DOSES_FRACIONADAS_TOOLTIP_TEXT}>
            <DecimalField
              name={name.qtDose}
              label='Quantidade total da dose'
              required
              maxLength={DOSE_MAX_LENGTH}
              precisao={DOSE_PRECISAO_DECIMAL}
              disabled={isDisabled}
              invalid={hasTotalDoseError}
            />
          </Tooltip>
        </Cell>
      )}

      <Cell size={4}>
        <UnidadeMedidaSelectField
          name={name.unidadeMedidaDose}
          label='Unidade de medida'
          onKeyUp={handleKeyEnter}
          required
          disabled={isDisabled}
        />
      </Cell>
    </>
  )
}
